import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, DoCheck, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatLegacySlideToggle as MatSlideToggle, MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { MatLegacyTabChangeEvent as MatTabChangeEvent } from '@angular/material/legacy-tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { DEFAULT_PRECISION, Utils } from '@app/_helpers';
import { AlgoStrategy, ConfigurationService, defaultAlgoParams, NgxToastrService, QuoteStoreService, StrategiesService, SymbolSearchService } from '@app/_shared/providers';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { RouteMap } from '@app/_shared/shared.structs';
import { STRATEGY_FORM_INVALID, STRATEGY_NO_SYMBOL_SELECTED } from '@app/_shared/strings';
import { PersonalDetailsService } from '@app/modules/trading/modules/profile/providers/personal-details/personal-details.service';
import { cloneDeep, floor } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { WebApiStrategyRunningType, WebApiTransactionType } from '../../../../utProto/webApiStrategyEnums_pb';
import { StrategyDefineComponent } from './components/strategy-define/strategy-define.component';
import { StrategySetupComponent } from './components/strategy-setup/strategy-setup.component';
import { StrategyTakeEntryComponent } from './components/strategy-take-entry/strategy-take-entry.component';
import { StrategyTakeExitComponent } from './components/strategy-take-exit/strategy-take-exit.component';
import { TextBuilderModeComponent } from './components/text-builder-mode/text-builder-mode.component';
import { EXECUTION_STYLE, StrategyFormBuildMode, StrategyFormBuildModeTemp } from './strategy.enums';

@Component({
    selector: 'app-strategy-form',
    templateUrl: './strategy-form.component.html',
    styleUrls: ['./strategy-form.component.scss']
})
export class StrategyFormComponent implements OnInit, DoCheck {
    isEditing = false;
    oldData: AlgoStrategy;
    StrategyFormBuildMode = StrategyFormBuildMode;
    StrategyFormBuildModeTemp: any;
    strategyMode$ = new BehaviorSubject<StrategyFormBuildMode>(StrategyFormBuildMode.REGULAR);
    EXECUTION_STYLE;
    isEditBacktest: boolean;
    executionStyleControl = new UntypedFormControl(EXECUTION_STYLE.OFFLINE, [Validators.required]);
    strategyModeControl = new UntypedFormControl(StrategyFormBuildMode.REGULAR, [Validators.required]);

    /**
     * === Ticket
     * RA-663
     *
     * === Issue
     *
     * When ever user comes to edit page (From saved algo or confirmation page).
     * Form first open's in regular mode then processes old algo Data and moved
     * to the type of algo form in which it was created.
     *
     * For Ex :- If we create and algo in text mode then comes to edit page, first regular
     * form opens and then text mode opens.
     *
     * === Fix
     *
     * Showing a loader until old data is processed.
     *
     * @memberof StrategyFormComponent
     */
    loading = 0;
    selectedIndex = 0;

    @ViewChild(MatSlideToggle) advanceToggleRef: MatSlideToggle;
    @ViewChild(StrategySetupComponent) strategySetupRef: StrategySetupComponent;
    @ViewChild(StrategyDefineComponent) strategyDefineRef: StrategyDefineComponent;
    @ViewChild(StrategyTakeEntryComponent) strategyTakeEntryRef: StrategyTakeEntryComponent;
    // @ViewChild(RepairConditionComponent) repairConditionRef: RepairConditionComponent;
    @ViewChild(StrategyTakeExitComponent) strategyTakeExitRef: StrategyTakeExitComponent;
    @ViewChild(TextBuilderModeComponent) textBuilderModeRef: TextBuilderModeComponent;

    constructor(
        private strategyService: StrategiesService,
        private activatedRoute: ActivatedRoute,
        private location: Location,
        private snackbar: NgxToastrService,
        private router: Router,
        private symbolSearchService: SymbolSearchService,
        private quoteService: QuoteStoreService,
        private configService: ConfigurationService,
        private profileService: PersonalDetailsService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.isEditBacktest = this.activatedRoute.snapshot.queryParams.comingFrom === RouteMap.editBacktestAlgo;
        try {
            const state = this.router.getCurrentNavigation().extras.state;
            if (state?.edit) {
                this.oldData = state.edit;
            }
        } catch (error) {

        }
    }

    ngOnInit(): void {
        this.strategyModeControl.valueChanges.subscribe(val => {
            this.strategyMode$.next(val);
        });

        this.strategyModeControl.setValue(defaultAlgoParams.strategyMode);

        this.isEditing = this.activatedRoute.snapshot.params.id ? true : false;


        this.StrategyFormBuildModeTemp = cloneDeep(Object(StrategyFormBuildModeTemp));


            this.activatedRoute.params.subscribe(params => {
                const strategyId = params.id;
                this.loading++;
                if (this.oldData) {
                    this.fillForm(this.oldData);
                } else if (strategyId) {
                    this.fillUpdateForm(strategyId);
                } else {
                    this.fillForm(defaultAlgoParams);
                }
            });

        this.loading++;
        setTimeout(() => {
            this.fillForm(this.oldData || defaultAlgoParams);
        }, 1000);
    }

    ngDoCheck(): void {
        this.changeDetectorRef.detectChanges()
    }

    strategyModeTab() {
        if (this.oldData?.strategyMode) {
            this.selectedIndex = Object.values(StrategyFormBuildModeTemp).indexOf(this.oldData.strategyMode as any);
        }
    }

    /**
     * This functions checks the plan which is subscribed and hides build options which are not enabled.
     * If this is a editing form, the previous build mode is not hidden and error is thrown on saving
     * if that plan was disabled.
     *
     * Example :- A user creates a wizard mode. After creating algo, admin disables wizard mode from UMS.
     * When user open edit form for that algo, The mode selection should contain wizard mode(where as
     * creation should not).
     * When user saves that algo, popup with error will be shown. User must change build mode to continue.
     *
     * @memberof StrategyFormComponent
     */
    updateBuildMode() {
        this.strategyModeControl.setValue(this.StrategyFormBuildModeTemp[Object.keys(this.StrategyFormBuildModeTemp)[0]]);
    }

    fillUpdateForm(strategyId: string) {
        this.strategyService.getStrategy(strategyId).subscribe((res: AlgoStrategy) => {
            if (!res) {
                this.location.back();
                return;
            }
            this.isEditing = true;
            this.oldData = res;
            this.fillForm(res);
        })
    }


    onStrategyModeChange(event: MatTabChangeEvent) {
        const value = Object.values(this.StrategyFormBuildModeTemp)[event.index];
        this.strategyModeControl.setValue(value);
        if (value === StrategyFormBuildMode.REGULAR) {
            this.strategyTakeEntryRef.fillUpdateForm(defaultAlgoParams);
            this.strategyTakeExitRef.fillUpdateForm(defaultAlgoParams);
        }
    }


    fillForm(form: AlgoStrategy) {
        // Setting tab acc. to strategy mode selected previously if there was any
        // triggered when user is edditing strategy.
        this.strategyModeTab();

        try {
            this.updateBuildMode();

            /**
             *  ======= TICKET ====
             * RA-562
             *
             * ======= Fix ===
             * In case "direct param" is set to 0 at webserver,
             * -> then user should not be able to create direct strategy.
             *    Therefore, hiding direct param in creation case.
             * -> But editing should be allowed in case the created algo was of
             *    "direct" style.
             */
            this.configService.getServerConfig().subscribe((config) => {
                this.EXECUTION_STYLE = cloneDeep(EXECUTION_STYLE);
                if (!config?.direct_enabled) {
                    if (!form.id || (form.executionStyle !== EXECUTION_STYLE.FULLY_AUTOMATED)) {
                        delete this.EXECUTION_STYLE.FULLY_AUTOMATED;
                    }
                }
            })

            if (Object.values(this.StrategyFormBuildModeTemp).includes(form.strategyMode)) {
                this.strategyMode$.next(form.strategyMode);
                this.strategyModeControl.setValue(form.strategyMode);
            }

            if (this.textBuilderModeRef && this.oldData && form.strategyMode === StrategyFormBuildMode.TEXT_BUILDER) {
                this.executionStyleControl.setValue(form.executionStyle);
                this.textBuilderModeRef.setTextValue(form);
            } else {
                this.executionStyleControl.setValue(form.executionStyle);
                this.strategySetupRef.fillUpdateForm(form);
                this.strategyDefineRef.fillUpdateForm(form);
                this.strategyTakeEntryRef.fillUpdateForm(form);
                this.strategyTakeExitRef.fillUpdateForm(form);
            }
            this.loading--;
        } catch (error) {
            this.loading--;
        }
    }

    toggleAdvanceForm(event: MatSlideToggleChange) {
        // this.strategyMode.next(event.checked);
    }

    getFormData() {
        this.executionStyleControl.markAllAsTouched();
        const strategyObject = new AlgoStrategy();
        const setupData = this.strategySetupRef.getData();
        const defineData = this.strategyDefineRef.getData();

        const takeEntryFromGroupArr = this.strategyTakeEntryRef.basicIndicatorForm?.formGroupArr;
        if (takeEntryFromGroupArr && takeEntryFromGroupArr.length) {
            takeEntryFromGroupArr[takeEntryFromGroupArr.length - 1].markAllAsTouched();
        }
        const entryData = this.strategyTakeEntryRef.getData();

        const takeExitFormGroupArr = this.strategyTakeExitRef.basicIndicatorFormRef?.formGroupArr;
        if (takeExitFormGroupArr && takeExitFormGroupArr.length) {
            takeExitFormGroupArr[takeExitFormGroupArr.length - 1].markAllAsTouched();
        }
        const exitData = this.strategyTakeExitRef.getData();

        const executionStyle = this.executionStyleControl.value;


        if (setupData && defineData && entryData && exitData && executionStyle) {

            // if (entryData.closeTime && defineData.automaticShutDownTime &&
            //     moment(entryData.closeTime).isAfter(moment(defineData.automaticShutDownTime))) {
            //     this.snackbar.simpleCustomToaster('Position close time should be less than shutdown time.');
            //     return;
            // } else if (entryData.closeTime && defineData.automaticShutDownTime && defineData.automaticStartTime) {
            //     const positionRetakeMoment = moment(entryData.closeTime);
            //     const shutDownMoment = moment(defineData.automaticShutDownTime);
            //     const startTimeMoment = moment(defineData.automaticStartTime);

            //     if (!positionRetakeMoment.isBetween(startTimeMoment, shutDownMoment)) {
            //         this.snackbar.simpleCustomToaster('Position close time should between start time and shutdown time.');
            //         return;
            //     }
            // }

            strategyObject.parseForm(
                this.strategyMode$.value,
                executionStyle,
                setupData,
                defineData,
                entryData,
                exitData,
                this.symbolSearchService
            )

            strategyObject.symbols.forEach((symbol, idx) => {
                const symbolInfo = this.symbolSearchService.symbolInfo(`${MarketIDMap[symbol.market_id]}_${symbol.security_id}`) || [1];
                const lotSize = symbolInfo[0];

                if (!strategyObject.isLot) {
                    const ltp = (Utils.applyPrecision(this.quoteService.getFeedObj(
                        MarketIDMap[symbol.market_id], symbol.security_id).LTP, DEFAULT_PRECISION)) || 1;
                    strategyObject.valueConvertedLot[idx] = floor(strategyObject.orderQty / (ltp * lotSize));
                } else {
                    strategyObject.valueConvertedLot[idx] = strategyObject.orderQty;
                }
            });

            // In case of edit strategy, token ID should be retained (It is used for all updation operations on WEB)
            if (this.oldData?.symbols?.length && this.oldData.symbols[0].token_id) {
                strategyObject.symbols[0].token_id = this.oldData.symbols[0].token_id;
            }

            return strategyObject;
        }
    }

    saveData() {
        // setting timeout so that other form groups present can check and activate their validations properly
        // before saving data
        setTimeout(() => {
            const strategyObject = this.getFormData();

            if (!this.isFinished()) {
                if (strategyObject && !strategyObject.symbols.length) {
                    this.snackbar.simpleCustomToaster(STRATEGY_NO_SYMBOL_SELECTED)
                } else {
                    this.snackbar.simpleCustomToaster(STRATEGY_FORM_INVALID);
                }
                return;
            }
            if (this.oldData) {
                strategyObject.algoName = this.oldData.algoName;
                strategyObject.algoNotes = this.oldData.algoNotes;
            }
            if (!strategyObject) {
                return;
            }

            this.profileService.isModeAllowed(strategyObject.executionStyle).subscribe((isAllowed) => {
                if (isAllowed) {
                    this.router.navigate(['/trading/strategies/confirmation'], {
                        state: {
                            algo: strategyObject
                        }
                    })
                } else {
                    this.snackbar.simpleCustomToaster(`${strategyObject.executionStyle} not allowed in your plan`);
                }
            })

        }, 300);
    }

    runBackTest() {
        const strategyObject = this.getFormData();
        if (!strategyObject) {
            return;
        }
        if (this.isEditing) {
            this.strategyService.updateStrategy(this.oldData.id, strategyObject).subscribe(res => {
                this.sendBacktestRequest(this.oldData.id);
            })
        } else {
            this.strategyService.saveStrategy(strategyObject).subscribe(res => {
                this.sendBacktestRequest(res.algo_id);
            })
        }
    }

    checkIfModeAllowed(mode: StrategyFormBuildMode) {
        return false
    }

    updateStrategy(isBacktest: boolean) {
        const strategyObject = this.getFormData();
        if (!strategyObject) {
            return;
        }
        strategyObject.algoName = this.oldData.algoName;
        strategyObject.algoNotes = this.oldData.algoNotes;
        strategyObject.id = this.oldData.id;

        // Retain old market palce settings. User can edit them in next screen.
        strategyObject.setMarketPlaceSettings(this.oldData);

        if (!this.checkIfModeAllowed(strategyObject.strategyMode)) {
            this.snackbar.simpleCustomToaster(`${strategyObject.strategyMode} mode has been disabled by admin for your plan.`);
            return;
        }

        this.profileService.isModeAllowed(strategyObject.executionStyle).subscribe((isAllowed) => {
            if (isAllowed) {
                if (!isBacktest) {
                    this.router.navigate(['/trading/strategies/confirmation'], {
                        state: {
                            algo: strategyObject
                        }
                    });
                } else {
                    this.saveBackTestStrategy(strategyObject);
                }
            } else {
                this.snackbar.simpleCustomToaster(`${strategyObject.executionStyle} not allowed in your plan`);
            }
        })
    }

    saveBackTestStrategy(strategy) {
        this.strategyService.updateStrategy(strategy.id, strategy).subscribe(res => {
            this.router.navigate(['/trading/backtest/detail/' + strategy.id + '/',], { queryParams: { comingFrom: RouteMap.createAlgo } });
        });
    }

    sendBacktestRequest(algoID: string) {
        this.strategyService.executeAlgo({
            algo_id: algoID,
            algo_type: WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING,
            state: WebApiTransactionType.WEBAPITRANSACTIONTYPE_NEW
        }).subscribe(res => {
            res.forEach(algoResponse => {
                this.snackbar.simpleCustomToaster(algoResponse.message)
            })
        })
    }

    isFinished() {
        return this.strategySetupRef?.isValid &&
            this.strategyDefineRef?.isValid &&
            this.strategyTakeEntryRef?.isValid &&
            this.strategyTakeExitRef?.isValid
    }
}
